import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchDonationPageConfig } from './api';
import DonationFlow from './DonationFlow';
import styles from './PublicDonation.module.css';
import { DonationCurrency } from './types';

function parseCurrency(raw: string | null): DonationCurrency | null {
  if (!raw) return null;
  const v = raw.toUpperCase();
  if (v === 'USD') return 'USD';
  if (v === 'ILS' || v === 'NIS') return 'ILS';
  return null;
}

function normalizeI18nLang(param: string | null): string | null {
  if (!param) return null;
  if (param === 'heb') return 'he';
  return param;
}

const PublicDonationPage = () => {
  const { fundId } = useParams<{ fundId: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const defaultCurrency = parseCurrency(searchParams.get('currency'));
  const langParam = normalizeI18nLang(searchParams.get('lang'));

  useEffect(() => {
    if (langParam && langParam !== i18n.language) i18n.changeLanguage(langParam);
  }, [langParam, i18n]);

  useEffect(() => {
    document.title = t('donations.pageTitle');
    return () => { document.title = ''; };
  }, [t]);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'he' ? 'en' : 'he');
  };

  const fundIdNum = Number(fundId);
  const { data: config, isLoading, isError } = useQuery({
    queryKey: ['donation-page-config', fundIdNum],
    queryFn: () => fetchDonationPageConfig(fundIdNum),
    enabled: !!fundIdNum,
  });

  if (isLoading) {
    return (
      <div className={styles.pageStandalone}>
        <div className={styles.card}>
          <div className={styles.loaderWrapper}>
            <div className={styles.spinner} />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !config) {
    return <div className={styles.pageStandalone}><div className={styles.card}><p className={styles.errorMessage}>{t('donations.loadError')}</p></div></div>;
  }

  return (
    <div className={styles.pageStandalone}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          {config.logoUrl && (
            <img src={config.logoUrl} alt="" className={styles.donationLogo} />
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <h1 className={styles.title}>{t('donations.donateTo', { id: config.fundId, name: config.fundName })}</h1>
            <button type="button" className={styles.langToggle} onClick={toggleLang}>
              {i18n.language === 'he' ? 'EN' : 'עב'}
            </button>
          </div>
        </div>
        <DonationFlow
          config={config}
          initialCurrency={defaultCurrency ?? 'ILS'}
          onSuccess={() => navigate(`/donate/success?lang=${i18n.language}&fundId=${config.fundId}`)}
        />
      </div>
    </div>
  );
};

export default PublicDonationPage;
